/**
 * S08 controller support (PLAN-CITY §5). Deliberately a STANDALONE poller,
 * not a change to `InputSystem`'s internals (`core/input.ts`) — S07 is
 * concurrently adding a `lefty` flag to that same class, and every existing
 * consumer (main.ts's tick()) already reads `input.move`/`consumeJump()`/
 * `consumeLook()` as plain values, so merging a second input source in at
 * the call site is strictly additive and carries zero risk of the two
 * concurrent edits touching the same lines.
 *
 * Standard mapping (Xbox-style; the Gamepad API's own `mapping: 'standard'`
 * guarantees this button/axis layout across browsers/OSes when present — with
 * no standard-mapped pad connected, `poll()` returns a frame with
 * `connected: false` rather than guessing at a non-standard layout):
 *   left stick  (axes 0,1)  -> move, same sign convention as InputSystem
 *                              (y negative = forward)
 *   right stick (axes 2,3)  -> look, scaled to roughly match a drag-pixel
 *                              delta so the SAME `sensitivity` constant
 *                              main.ts already applies to touch/mouse drag
 *                              works for the stick too
 *   A (button 0)  -> jump        B (button 1)  -> slide/dodge
 *   X (button 2)  -> interact    Y (button 3)  -> weapon cycle
 *   LB (button 4) -> aim-preset cycle
 *   RT (button 7) -> super
 */

const DEAD_ZONE = 0.12; // matches VirtualStick's own DEAD_ZONE (core/input.ts) so both input sources feel identical
const LOOK_AXIS_SCALE = 12; // right-stick units -> "drag pixels/tick" equivalent, tuned so a full-deflection stick turns at roughly the same rate a brisk thumb drag does

function applyDeadzone(v: number): number {
  const mag = Math.abs(v);
  if (mag < DEAD_ZONE) return 0;
  const eased = (mag - DEAD_ZONE) / (1 - DEAD_ZONE);
  return Math.sign(v) * Math.min(1, eased);
}

export interface GamepadFrame {
  connected: boolean;
  moveX: number;
  moveY: number;
  lookDx: number;
  lookDy: number;
  jumpPressed: boolean;
  slidePressed: boolean;
  interactPressed: boolean;
  weaponCyclePressed: boolean;
  aimCyclePressed: boolean;
  superPressed: boolean;
}

const EMPTY_FRAME: GamepadFrame = {
  connected: false,
  moveX: 0,
  moveY: 0,
  lookDx: 0,
  lookDy: 0,
  jumpPressed: false,
  slidePressed: false,
  interactPressed: false,
  weaponCyclePressed: false,
  aimCyclePressed: false,
  superPressed: false,
};

const MAX_TRACKED_BUTTONS = 20; // generous — the standard Gamepad mapping defines 17

/**
 * Polls `navigator.getGamepads()` and edge-triggers its own button state
 * (same "consume once per physical press" convention as `InputSystem`'s
 * `JumpButton`/`Hud`'s cycle buttons) so a held button doesn't repeat-fire
 * every tick. Call `poll()` once per tick; the returned frame's press flags
 * are already edge-triggered — no separate `consume*()` step needed.
 *
 * S08 memory audit (applied to this module before it ever shipped, same
 * discipline as the ai/enemy.ts, ai/steering.ts, core/camera.ts, and
 * combat/autoaim.ts fixes elsewhere in this session): the first draft
 * allocated a fresh `Set<number>` AND a fresh `GamepadFrame` object literal
 * on every single call — unconditionally every tick, same class of GC churn
 * those fixes targeted. Both are now reused instance state (a fixed-size
 * boolean array instead of a Set — bounded by MAX_TRACKED_BUTTONS, cheaper
 * than Set's hashing for such a small, dense index range — and one mutated
 * `GamepadFrame`), safe for the same reason as those other fixes: the
 * returned frame is read synchronously within the same tick by main.ts and
 * never stored across ticks.
 */
export class GamepadInput {
  private readonly prevPressed = new Array<boolean>(MAX_TRACKED_BUTTONS).fill(false);
  private readonly frame: GamepadFrame = { ...EMPTY_FRAME };
  // S08 correctness-review finding (LOW): `poll()` always selects the FIRST
  // standard-mapped pad in `navigator.getGamepads()` order — if that pad
  // disconnects while a second standard-mapped pad stays connected, the
  // next poll silently starts reading the second pad's buttons against
  // `prevPressed` state that was actually built from the first pad's press
  // history, which can swallow one real edge if the two happened to share a
  // held-button state at that exact transition. Tracking the selected pad's
  // own stable `index` and resetting `prevPressed` on any identity change
  // (not just full disconnect) closes this.
  private selectedPadIndex: number | null = null;

  poll(): GamepadFrame {
    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    let pad: Gamepad | null = null;
    for (const p of pads) {
      if (p && p.mapping === 'standard') {
        pad = p;
        break;
      }
    }
    if (!pad) {
      this.prevPressed.fill(false);
      this.selectedPadIndex = null;
      Object.assign(this.frame, EMPTY_FRAME);
      return this.frame;
    }
    if (pad.index !== this.selectedPadIndex) {
      this.prevPressed.fill(false); // a DIFFERENT pad took the slot — its held-button state, if any, must count as fresh, not compared against the previous pad's history
      this.selectedPadIndex = pad.index;
    }

    const edge = (i: number): boolean => {
      const now = pad.buttons[i]?.pressed ?? false;
      const wasPressed = this.prevPressed[i];
      this.prevPressed[i] = now;
      return now && !wasPressed;
    };
    const f = this.frame;
    f.connected = true;
    f.moveX = applyDeadzone(pad.axes[0] ?? 0);
    f.moveY = applyDeadzone(pad.axes[1] ?? 0);
    f.lookDx = applyDeadzone(pad.axes[2] ?? 0) * LOOK_AXIS_SCALE;
    f.lookDy = applyDeadzone(pad.axes[3] ?? 0) * LOOK_AXIS_SCALE;
    f.jumpPressed = edge(0);
    f.slidePressed = edge(1);
    f.interactPressed = edge(2);
    f.weaponCyclePressed = edge(3);
    f.aimCyclePressed = edge(4);
    f.superPressed = edge(7);
    return f;
  }
}
